import {
  createContext, useCallback, useContext, useEffect, useMemo, useState,
} from 'react';
import { useLocation } from 'react-router-dom';
import type { Tour } from './types';
import { findTourById, findTourByRoute } from './tours';
import { TourOverlay } from './TourOverlay';

const SEEN_PREFIX = 'myshow.tour.seen.';

function hasSeen(id: string): boolean {
  try { return localStorage.getItem(SEEN_PREFIX + id) === '1'; } catch { return false; }
}
function markSeen(id: string) {
  try { localStorage.setItem(SEEN_PREFIX + id, '1'); } catch { /* noop */ }
}

interface TourContextValue {
  /** inicia um tour pelo id (uso manual: botão "rever tour") */
  startTour: (id: string) => void;
  /** existe tour para a rota atual? */
  tourForCurrentRoute: () => Tour | undefined;
  active: boolean;
}

const TourContext = createContext<TourContextValue | null>(null);

export function useTour() {
  const ctx = useContext(TourContext);
  if (!ctx) throw new Error('useTour deve ser usado dentro de <TourProvider>');
  return ctx;
}

export function TourProvider({ children }: { children: React.ReactNode }) {
  const location = useLocation();
  const [activeTour, setActiveTour] = useState<Tour | null>(null);

  const startTour = useCallback((id: string) => {
    const tour = findTourById(id);
    if (tour) setActiveTour(tour);
  }, []);

  const stop = useCallback(() => {
    if (activeTour) markSeen(activeTour.id);
    setActiveTour(null);
  }, [activeTour]);

  const tourForCurrentRoute = useCallback(
    () => findTourByRoute(location.pathname),
    [location.pathname],
  );

  // Auto-run no primeiro acesso de uma rota que tenha tour com autoRun.
  // Sem ref-guard de propósito: sob React.StrictMode o efeito monta→limpa→monta,
  // e um guard faria o 2º disparo sair cedo após o 1º timeout ser cancelado.
  // hasSeen() + (cur ?? tour) já garantem que não rode duas vezes nem reabra.
  useEffect(() => {
    const tour = findTourByRoute(location.pathname);
    if (!tour || !tour.autoRun) return;
    if (hasSeen(tour.id)) return;
    // pequeno atraso pra página renderizar os elementos-alvo
    const t = setTimeout(() => setActiveTour((cur) => cur ?? tour), 900);
    return () => clearTimeout(t);
  }, [location.pathname]);

  const value = useMemo<TourContextValue>(
    () => ({ startTour, tourForCurrentRoute, active: !!activeTour }),
    [startTour, tourForCurrentRoute, activeTour],
  );

  return (
    <TourContext.Provider value={value}>
      {children}
      {activeTour && <TourOverlay tour={activeTour} onClose={stop} />}
    </TourContext.Provider>
  );
}
